import { offlineStorage } from './offlineStorage';
import { supabaseCustomerStorage, supabaseLocationStorage, supabaseContactStorage, supabaseBookingStorage } from './supabaseStorage';
import { localCustomerStorage, localBookingStorage } from './localStorageCache';

/**
 * Offline Sync Manager
 * Handles syncing queued mutations when connection is restored
 */

interface SyncResult {
  success: boolean;
  mutationId: string;
  error?: string;
}

class OfflineSyncManager {
  private isSyncing: boolean = false;
  private syncListeners: Array<(isSyncing: boolean) => void> = [];
  private lastSyncTime: Date | null = null;

  constructor() {
    // Auto-sync when online
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.sync();
      });
    }
  }

  // Subscribe to sync status changes
  onSyncStatusChange(listener: (isSyncing: boolean) => void): () => void {
    this.syncListeners.push(listener);
    return () => {
      this.syncListeners = this.syncListeners.filter(l => l !== listener);
    };
  }

  private notifySyncStatus(isSyncing: boolean): void {
    this.syncListeners.forEach(listener => listener(isSyncing));
  }

  // Sync all pending mutations
  async sync(): Promise<{ success: number; failed: number; errors: string[] }> {
    if (this.isSyncing) {
      console.log('⏳ Sync already in progress');
      return { success: 0, failed: 0, errors: [] };
    }

    // Check if we're online
    if (!navigator.onLine) {
      console.log('📴 Offline - cannot sync');
      return { success: 0, failed: 0, errors: ['Not online'] };
    }

    this.isSyncing = true;
    this.notifySyncStatus(true);

    try {
      const pendingMutations = await offlineStorage.getPendingMutations();
      
      if (pendingMutations.length === 0) {
        console.log('✅ No pending mutations to sync');
        this.isSyncing = false;
        this.notifySyncStatus(false);
        return { success: 0, failed: 0, errors: [] };
      }

      console.log(`🔄 Syncing ${pendingMutations.length} pending mutations...`);

      const results: SyncResult[] = [];
      const errors: string[] = [];

      // Process mutations sequentially to avoid conflicts
      for (const mutation of pendingMutations) {
        try {
          await this.processMutation(mutation);
          results.push({ success: true, mutationId: mutation.id });
          await offlineStorage.markMutationSynced(mutation.id);
        } catch (error: any) {
          const errorMessage = error?.message || 'Unknown error';
          errors.push(`${mutation.type} (${mutation.id}): ${errorMessage}`);
          results.push({ 
            success: false, 
            mutationId: mutation.id, 
            error: errorMessage 
          });
          console.error(`❌ Failed to sync mutation ${mutation.id}:`, error);
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failedCount = results.filter(r => !r.success).length;

      console.log(`✅ Sync complete: ${successCount} succeeded, ${failedCount} failed`);

      // Invalidate local caches after successful sync (avoid full-table re-download)
      if (successCount > 0) {
        await this.invalidateLocalCacheAfterSync();
      }

      // Clean up old synced mutations
      await offlineStorage.clearSyncedMutations();

      this.lastSyncTime = new Date();
      this.isSyncing = false;
      this.notifySyncStatus(false);

      return { success: successCount, failed: failedCount, errors };
    } catch (error: any) {
      console.error('❌ Sync failed:', error);
      this.isSyncing = false;
      this.notifySyncStatus(false);
      return { 
        success: 0, 
        failed: 0, 
        errors: [error?.message || 'Unknown sync error'] 
      };
    }
  }

  // Process a single mutation
  private async processMutation(mutation: any): Promise<void> {
    switch (mutation.type) {
      case 'customer':
        await this.syncCustomerMutation(mutation);
        break;
      case 'location':
        await this.syncLocationMutation(mutation);
        break;
      case 'contact':
        await this.syncContactMutation(mutation);
        break;
      case 'booking':
        await this.syncBookingMutation(mutation);
        break;
      default:
        throw new Error(`Unknown mutation type: ${mutation.type}`);
    }
  }

  private async syncCustomerMutation(mutation: any): Promise<void> {
    const { data } = mutation;
    
    switch (mutation.data.operation) {
      case 'create':
        await supabaseCustomerStorage.addCustomer(data);
        break;
      case 'update':
        await supabaseCustomerStorage.updateCustomer(data);
        break;
      case 'delete':
        await supabaseCustomerStorage.deleteCustomer(data.id);
        break;
      default:
        throw new Error(`Unknown customer operation: ${mutation.data.operation}`);
    }
  }

  private async syncLocationMutation(mutation: any): Promise<void> {
    const { customerId, location } = mutation.data;
    
    switch (mutation.data.operation) {
      case 'create':
        await supabaseLocationStorage.addLocation(customerId, location);
        break;
      case 'update':
        await supabaseLocationStorage.updateLocation(customerId, location);
        break;
      case 'delete':
        await supabaseLocationStorage.deleteLocation(customerId, location.id);
        break;
      default:
        throw new Error(`Unknown location operation: ${mutation.data.operation}`);
    }
  }

  private async syncContactMutation(mutation: any): Promise<void> {
    const { customerId, contact } = mutation.data;
    
    switch (mutation.data.operation) {
      case 'create':
        await supabaseContactStorage.addContact(customerId, contact);
        break;
      case 'update':
        await supabaseContactStorage.updateContact(customerId, contact);
        break;
      case 'delete':
        await supabaseContactStorage.deleteContact(customerId, contact.id);
        break;
      default:
        throw new Error(`Unknown contact operation: ${mutation.data.operation}`);
    }
  }

  private async syncBookingMutation(mutation: any): Promise<void> {
    const { booking } = mutation.data;
    
    switch (mutation.data.operation) {
      case 'create':
        await supabaseBookingStorage.addBooking(booking);
        break;
      case 'update':
        await supabaseBookingStorage.updateBooking(booking.id, booking);
        break;
      case 'delete':
        await supabaseBookingStorage.deleteBooking(booking.id);
        break;
      default:
        throw new Error(`Unknown booking operation: ${mutation.data.operation}`);
    }
  }

  /** Clear cached full CRM snapshots so the next screen load uses paginated / scoped fetches. */
  private async invalidateLocalCacheAfterSync(): Promise<void> {
    try {
      const { localCustomerStorage, localBookingStorage } = await import('./localStorageCache');
      localCustomerStorage.clear();
      localBookingStorage.clear();
      await offlineStorage.clearEntityStore('customers');
      await offlineStorage.clearEntityStore('bookings');
      console.log('✅ Local CRM caches invalidated after offline sync');
    } catch (error) {
      console.error('Failed to invalidate local cache:', error);
    }
  }

  // Get sync status
  getStatus(): {
    isSyncing: boolean;
    lastSyncTime: Date | null;
  } {
    return {
      isSyncing: this.isSyncing,
      lastSyncTime: this.lastSyncTime,
    };
  }

  // Manual sync trigger
  async manualSync(): Promise<{ success: number; failed: number; errors: string[] }> {
    return this.sync();
  }
}

export const offlineSyncManager = new OfflineSyncManager();


